import Debugger from 'utils/debugger';
import { CONTRACT, SECTION } from 'constants/global';
import { captureMercadoPagoError } from 'utils/sendSentryLog';
import MercadoPagoApi from './repository';
import BancomerApi from '../bancomer/repository';

/**
 * Obtiene los datos del usuario (tarjetas guardadas) desde el backend.
 * @param {string} email - Email del usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<object>} - Objeto con { cards: Array } o { cards: [] } en error
 */
export async function getUserData(email, userId) {
  try {
    const response = await MercadoPagoApi.getUserData({
      email,
      user_id: userId,
    });
    return response;
  } catch (error) {
    Debugger.error(error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'getUserData',
      userId,
      email,
    });
    return { cards: [] };
  }
}

/**
 * Guarda una tarjeta. Si falla, almacena los datos en un stage para procesarlos después
 * @param {string} userId - ID del usuario
 * @param {string} token - Token de la tarjeta
 * @param {object} cardData - Datos adicionales de la tarjeta para el stage (opcional)
 * @returns {Promise<object>} - Respuesta con success: true si se guardó o se almacenó en stage
 */
export async function saveCard(userId, token, cardData = {}) { // eslint-disable-line no-unused-vars -- reservado para stage
  try {
    const response = await MercadoPagoApi.saveCard({
      userId,
      token,
    });
    return response;
  } catch (error) {
    Debugger.error('[saveCard] Error al guardar tarjeta, almacenando en stage', error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'saveCard',
      userId,
    });
    throw new Error(error.message || 'Error al guardar tarjeta');
  }
}

/**
 * Actualiza una tarjeta guardada.
 * @param {string} userId - ID del usuario
 * @param {string} cardId - ID de la tarjeta a actualizar
 * @param {string} token - Token de la tarjeta generado por Mercado Pago
 * @returns {Promise<object>} - Respuesta con success: true y datos de la tarjeta actualizada
 */
export async function updateCard(userId, cardId, token) {
  try {
    const response = await MercadoPagoApi.updateCard({
      cardId,
      userId,
      token,
    });
    return { success: true, data: response };
  } catch (error) {
    Debugger.error('[updateCard] Error al actualizar tarjeta', error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'updateCard',
      userId,
    });
    return { success: false, message: error.message || 'Error al actualizar la tarjeta' };
  }
}

/**
 * Elimina una tarjeta guardada.
 * @param {string} userId - ID del usuario
 * @param {string} cardId - ID de la tarjeta a eliminar
 * @returns {Promise<object>} - Respuesta con success: true y { status, statusDetail }
 */
export async function deleteCard(userId, cardId) {
  try {
    const response = await MercadoPagoApi.deleteCard({
      cardId,
      userId,
    });
    return { success: true, status: response.status, statusDetail: response.statusDetail };
  } catch (error) {
    Debugger.error('[deleteCard] Error al eliminar tarjeta', error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'deleteCard',
      userId,
    });
    return { success: false, message: error.message || 'Error al eliminar la tarjeta' };
  }
}

/**
 * Procesa un pago único. El backend espera en paymentMetadata el campo cardToken.
 * @param {object} paymentData - Datos del pago (token, amount, paymentMethodId, email, etc.)
 * @returns {Promise<object>} - Respuesta del API (success, data con status, etc.)
 */
export async function processPayment(paymentData) {
  try {
    const {
      token,
      amount,
      monto,
      paymentMethodId,
      email,
      issuer = '',
      cardNumber = '',
      cardholderName = '',
      externalReference,
      contractType = CONTRACT,
      cardType = '',
      id,
      paymentKey = null,
      paymentName = '',
      coverageId = 0,
      coverage = 0,
    } = paymentData;

    // Generar externalOrder si no se proporciona
    const externalOrder = externalReference || `EXT-${Date.now()}-${id || ''}`;

    // Formatear amount y monto igual que en BBVA: amount = capital, monto = concepto (payment.amount)
    const formattedAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
    const parsedMonto = typeof monto === 'number' ? monto : parseFloat(monto);
    const formattedMonto = Number.isNaN(parsedMonto) ? formattedAmount : parsedMonto;

    // Determinar tipo de contrato y a qué campo va el id (similar a BBVA)
    const finalContractType = contractType || CONTRACT;
    const finalContractId = finalContractType === CONTRACT ? (id || null) : null;
    const finalSectionId = finalContractType === SECTION ? (id || null) : null;

    // Construir el payload según la estructura esperada por el backend
    const payload = {
      coverage: coverage || 0,
      paymentKey: paymentKey || null,
      monto: formattedMonto,
      contractType: finalContractType,
      os: window.getOS() || 'WEB',
      paymentMetadata: {
        cardToken: token || '',
        cardNumber: cardNumber || '',
        cardType: cardType || '',
        externalOrder,
        paymentDescription: `Pago de ${formattedMonto} - ${cardholderName || email || 'Usuario'}`,
        issuerId: issuer || null,
        paymentMethodId: paymentMethodId || '',
      },
      amount: formattedAmount,
      contract_id: finalContractId,
      section_id: finalSectionId,
      payment_name: paymentName || '',
      coverage_id: coverageId || 0,
    };

    // Debugger.info('payload', payload);
    const response = await MercadoPagoApi.processPayment(payload);
    return response;
    // return { success: true, message: 'Pago procesado correctamente' };
  } catch (error) {
    Debugger.error(error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'processPayment',
      email: paymentData?.email,
      amount: paymentData?.amount,
    });
    return { success: false, message: error.message || 'Error al procesar el pago' };
  }
}

/**
 * Consulta el estado de un pago en MercadoPago
 * @param {string|number} paymentId - ID del pago a consultar
 * @returns {Promise<object>} - Respuesta con el estado del pago
 */
export async function getPaymentStatus(paymentId) {
  try {
    if (!paymentId) {
      throw new Error('paymentId es requerido');
    }

    const response = await MercadoPagoApi.getPaymentStatus(paymentId);
    return response;
  } catch (error) {
    Debugger.error('[getPaymentStatus] Error al consultar estado del pago:', error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'getPaymentStatus',
      paymentId,
    });
    return { success: false, message: error.message || 'Error al consultar el estado del pago' };
  }
}

/**
 * Procesa un pago múltiple. El backend espera en paymentMetadata el campo cardToken.
 * El componente envía el token en paymentMetadata.token; aquí se mapea a cardToken para el API.
 * @param {object} multiplePaymentData - { totalAmount, orders, paymentMetadata: { token, cardNumber, ... }, os }
 * @returns {Promise<object>} - Respuesta del API
 */
export async function processMultiplePayment(multiplePaymentData) {
  try {
    const {
      totalAmount,
      orders,
      paymentMetadata = {},
      os = window.getOS(),
    } = multiplePaymentData;

    if (typeof totalAmount === 'undefined' || !orders || !Array.isArray(orders)) {
      throw new Error('totalAmount y orders son requeridos');
    }

    const payload = {
      totalAmount,
      orders,
      os: os || window.getOS() || 'WEB',
      paymentMetadata: {
        cardToken: paymentMetadata.token || paymentMetadata.cardToken || '',
        cardNumber: paymentMetadata.cardNumber || '',
        cardType: paymentMetadata.cardType || '',
        externalOrder: paymentMetadata.externalOrder || `EXT-${Date.now()}`,
        paymentDescription: paymentMetadata.paymentDescription || `Pago múltiple de ${totalAmount}`,
        paymentMethodId: paymentMetadata.paymentMethodId || '',
      },
    };

    const response = await MercadoPagoApi.processMultiplePayment(payload);
    return response;
  } catch (error) {
    Debugger.error('[processMultiplePayment] Error al procesar pago múltiple:', error);
    captureMercadoPagoError(error, {
      feature: 'actions',
      action: 'processMultiplePayment',
      amount: multiplePaymentData?.totalAmount,
    });
    return { success: false, message: error.message || 'Error al procesar el pago múltiple' };
  }
}

export function getMercadoPagoPDF(externalOrder) {
  return BancomerApi.getBancomerPDF(externalOrder)
    .then((blob) => {
      window.share('mercado-pago', blob, 'application/pdf');
    });
}
